import {
  Action,
  action,
  Computed,
  computed,
  Thunk,
  thunk,
  thunkOn,
  ThunkOn
} from "easy-peasy";
import { useStoreActions, useStoreState } from "../hooks/ep";
import { Injections } from "./store-injections";
import { StoreModel } from "./model";

export interface TripLeg {
  TripId: number;
  leg_i: number;
}

export interface OpenReportActionModalPayload {
  actionName: string;
  snapshot: any;
}

export interface OpenViewFlopptionsModalPayload {
  snapshot: any;
}

// ra === "Report Action"
// vf === "View Flopptions"

export interface ModelMisc {
  NAME: string;
  //
  savedAppMode: null | string;
  appMode: Computed<ModelMisc, null | string>;
  availableAppModes: null | string[];
  setSavedAppMode: Action<ModelMisc, string>;
  setAvailableAppModes: Action<ModelMisc, null | string[]>;
  toggleAppMode: Thunk<ModelMisc, void, Injections, StoreModel>;
  //
  onLogin: ThunkOn<ModelMisc, Injections, StoreModel>;
  onResetMeData: ThunkOn<ModelMisc, Injections, StoreModel>;
  //
  savedItinMode: null | string;
  itinMode: Computed<ModelMisc, null | string>;
  availableItinModes: Computed<ModelMisc, string[]>;
  setSavedItinMode: Action<ModelMisc, string>;
  toggleItinMode: Thunk<ModelMisc, void, Injections, StoreModel>;
  //
  fullScreenModeEnabled: boolean;
  enableFullScreenMode: Action<ModelMisc>;
  disableFullScreenMode: Action<ModelMisc>;
  toggleFullScreenMode: Action<ModelMisc>;
  //
  ra_payload: OpenReportActionModalPayload | null;
  ra_openModal: Action<ModelMisc, OpenReportActionModalPayload>;
  ra_closeModal: Action<ModelMisc>;
  vf_activeTripLeg: Computed<ModelMisc, TripLeg | null>;
  vf_payload: OpenViewFlopptionsModalPayload | null;
  vf_openModal: Action<ModelMisc, OpenViewFlopptionsModalPayload>;
  vf_closeModal: Action<ModelMisc>;
}

export function snapshot2tripleg(snapshot) {
  return snapshot && { TripId: snapshot.TripId, leg_i: snapshot.leg_i };
}

function maybeGetSavedAppMode() {
  // Null values are: "", undefined
  const savedAppMode = window.localStorage.getItem("appMode");
  if (savedAppMode && savedAppMode.length > 0) {
    return savedAppMode;
  } else {
    return null;
  }
}

function maybeGetSavedItinMode() {
  // Null values are: "", undefined
  const savedItinMode = window.localStorage.getItem("itinMode");
  if (savedItinMode && savedItinMode.length > 0) {
    return savedItinMode;
  } else {
    return null;
  }
}

const miscModel: ModelMisc = {
  NAME: "misc",
  //
  savedAppMode: maybeGetSavedAppMode(),
  appMode: computed(
    [s => s.savedAppMode, s => s.availableAppModes],
    (savedAppMode, availableAppModes) => {
      if (
        availableAppModes &&
        (!savedAppMode || availableAppModes.includes(savedAppMode))
      ) {
        return (
          savedAppMode ?? (availableAppModes && availableAppModes[0]) ?? null
        );
      } else {
        return null;
      }
    }
  ),
  availableAppModes: null,
  setSavedAppMode: action((state, appMode) => {
    state.savedAppMode = appMode;
  }),
  setAvailableAppModes: action((state, availableAppModes) => {
    state.availableAppModes = availableAppModes;
  }),
  toggleAppMode: thunk((actions, payload, { getState }) => {
    const state = getState();
    const appMode = state.appMode;
    const itinMode = state.itinMode;

    const newAppMode = appMode === "dvy" ? "xca" : "dvy";

    if (newAppMode === "dvy" && itinMode === "roundtrip") {
      // "roundtrip" is not a valid itinMode when appMode is "dvy"
      const newItinMode = "oneway";
      window.localStorage.setItem("itinMode", newItinMode);
      actions.setSavedItinMode(newItinMode);
    }

    window.localStorage.setItem("appMode", newAppMode);
    actions.setSavedAppMode(newAppMode);

    actions.disableFullScreenMode();
    actions.ra_closeModal();
    actions.vf_closeModal();
  }),
  //
  savedItinMode: maybeGetSavedItinMode(),
  itinMode: computed(
    [s => s.appMode, s => s.savedItinMode, s => s.availableItinModes],
    (appMode, savedItinMode, availableItinModes) => {
      if (
        appMode &&
        availableItinModes &&
        (!savedItinMode || availableItinModes.includes(savedItinMode))
      ) {
        return (
          savedItinMode ?? (availableItinModes && availableItinModes[0]) ?? null
        );
      } else {
        return null;
      }
    }
  ),
  availableItinModes: computed([s => s.appMode], appMode => {
    if (appMode === "xca") return ["oneway", "roundtrip"];
    else return ["oneway"];
  }),
  setSavedItinMode: action((state, itinMode) => {
    state.savedItinMode = itinMode;
  }),
  toggleItinMode: thunk((actions, payload, { getState }) => {
    const newItinMode =
      getState().itinMode === "oneway" ? "roundtrip" : "oneway";
    window.localStorage.setItem("itinMode", newItinMode);
    actions.setSavedItinMode(newItinMode);
    actions.disableFullScreenMode();
    actions.ra_closeModal();
    actions.vf_closeModal();
  }),
  //
  onLogin: thunkOn(
    (__, storeActions) => storeActions.me.receiveInitialData,
    (actions, target, { getStoreState }) => {
      const availableAppModes = target.payload.available_app_modes;
      actions.setAvailableAppModes(availableAppModes);
      const miscState = getStoreState().misc;
      const savedAppMode = miscState.savedAppMode;
      const savedAppModeIsInvalid =
        savedAppMode && !availableAppModes.includes(savedAppMode);
      if (savedAppModeIsInvalid) {
        if (savedAppModeIsInvalid) window.localStorage.removeItem("appMode");
        actions.setSavedAppMode(null);
        actions.disableFullScreenMode();
        actions.ra_closeModal();
        actions.vf_closeModal();
      }
    }
  ),
  onResetMeData: thunkOn(
    (__, storeActions) => storeActions.me.resetData,
    actions => {
      window.localStorage.removeItem("appMode");
      actions.setSavedAppMode(null);
      actions.setAvailableAppModes(null);

      window.localStorage.removeItem("itinMode");
      actions.setSavedItinMode(null);

      actions.disableFullScreenMode();
      actions.ra_closeModal();
      actions.vf_closeModal();
    }
  ),
  //
  fullScreenModeEnabled: false,
  enableFullScreenMode: action(state => {
    state.fullScreenModeEnabled = true;
  }),
  disableFullScreenMode: action(state => {
    state.fullScreenModeEnabled = false;
  }),
  toggleFullScreenMode: action(state => {
    state.fullScreenModeEnabled = !state.fullScreenModeEnabled;
  }),
  //
  vf_activeTripLeg: computed(state => {
    return snapshot2tripleg(state.vf_payload?.snapshot);
  }),
  //
  ra_payload: null,
  ra_openModal: action((state, ra_payload) => {
    state.ra_payload = ra_payload;
  }),
  ra_closeModal: action(state => {
    state.ra_payload = null;
  }),
  //
  //
  vf_payload: null,
  vf_openModal: action((state, vf_payload) => {
    state.vf_payload = vf_payload;
  }),
  vf_closeModal: action(state => {
    state.vf_payload = null;
  })
};

export const FULLSCREEN_TABLES = new Set([
  "opportunities",
  "dead_opps",
  "bookings",
  "skippings",
  "suspends",
  "plannerStats"
]);

export const useAppMode = () => useStoreState(s => s.misc.appMode);
export const useItinMode = () => useStoreState(s => s.misc.itinMode);
export const useAvailableAppModes = () =>
  useStoreState(s => s.misc.availableAppModes);
export const useAvailableItinModes = () =>
  useStoreState(s => s.misc.availableItinModes);
export const useActiveTripLeg = () =>
  useStoreState(s => s.misc.vf_activeTripLeg);
export const useRaPayload = () => useStoreState(a => a.misc.ra_payload);
export const useVfPayload = () => useStoreState(s => s.misc.vf_payload);
export const useFullScreenModeEnabled = () =>
  useStoreState(s => s.misc.fullScreenModeEnabled);

export const useToggleAppMode = () =>
  useStoreActions(s => s.misc.toggleAppMode);
export const useToggleItinMode = () =>
  useStoreActions(s => s.misc.toggleItinMode);
export const useRaOpenModal = () => useStoreActions(a => a.misc.ra_openModal);
export const useVfOpenModal = () => useStoreActions(a => a.misc.vf_openModal);
export const useHideForm = () => useStoreActions(a => a.misc.ra_closeModal);
export const useVfCloseModal = () => useStoreActions(a => a.misc.vf_closeModal);

export default miscModel;
